import type { CustomFieldObject } from '@clockwise/clients/custom-fields';
import { ApiError } from '@clockwise/shared';
import { useEffect, useState } from 'react';

export type CustomFieldValuesState = Record<string, unknown>;

interface UseObjectCustomFieldsResult {
  /** Current draft values. Drives the editor's `values` prop. */
  values: CustomFieldValuesState;
  /** Setter to pass into the editor's `onChange`. */
  setValues: (next: CustomFieldValuesState) => void;
  /** Field-level errors keyed `customFields.<id>`. Drives the editor's `errors` prop. */
  errors: Record<string, string>;
  /**
   * Pulls `customFields.*` paths out of an `ApiError`'s field list and routes
   * them into local error state. Returns `true` if at least one custom-field
   * error was found (so the caller can decide whether to fall through to
   * host-form error routing for the remaining paths).
   *
   * Usage in the host edit page's mutation onError:
   *
   *   onError(err) {
   *     const hadCfErrors = cf.handleApiError(err);
   *     if (err instanceof ApiError && err.fields?.length) {
   *       for (const fe of err.fields) {
   *         if (fe.path.startsWith('customFields.')) continue;
   *         if (isCustomerFormField(fe.path)) form.setError(fe.path, { message: fe.message });
   *       }
   *     }
   *     if (hadCfErrors) toast.error('Please fix the highlighted fields');
   *   }
   */
  handleApiError: (err: unknown) => boolean;
  /** Spread onto `<CustomFieldsEditor>` — wires up object/values/onChange/errors. */
  EditorProps: {
    object: CustomFieldObject;
    values: CustomFieldValuesState;
    onChange: (next: CustomFieldValuesState) => void;
    errors: Record<string, string>;
  };
  /** Clear local errors before a fresh submit. */
  reset: () => void;
}

/**
 * Bundles the state + error-routing dance every host edit page needs to
 * drop in a `<CustomFieldsEditor>`. Initializes from the loaded row's
 * `customFields` (re-runs on row change), exposes a `handleApiError`
 * helper that pulls `customFields.*` paths out of a `ValidationError`
 * envelope, and yields an `EditorProps` object to spread onto the
 * component.
 *
 * Replaces three near-identical `useState + useEffect + onError` blocks
 * in users.$id.edit.tsx, customers.$id.edit.tsx, and the SOW custom-fields
 * panel — adding a new host just calls the hook with its `object` literal.
 *
 * @param object — Which host kind we're editing (`user`/`customer`/`sow`).
 * @param initial — The host row's stored `customFields` blob; the hook
 *   re-initializes when `initial` changes (typical: after the detail
 *   query resolves or after a successful save invalidates the cache).
 */
export function useObjectCustomFields(
  object: CustomFieldObject,
  initial: unknown,
): UseObjectCustomFieldsResult {
  const [values, setValues] = useState<CustomFieldValuesState>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setValues((initial ?? {}) as CustomFieldValuesState);
    setErrors({});
  }, [initial]);

  const handleApiError = (err: unknown): boolean => {
    if (!(err instanceof ApiError) || !err.fields?.length) return false;
    const next: Record<string, string> = {};
    let touched = false;
    for (const fe of err.fields) {
      if (fe.path.startsWith('customFields.')) {
        next[fe.path] = fe.message;
        touched = true;
      }
    }
    if (touched) setErrors(next);
    return touched;
  };

  const reset = () => setErrors({});

  return {
    values,
    setValues,
    errors,
    handleApiError,
    EditorProps: { object, values, onChange: setValues, errors },
    reset,
  };
}
